import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import {
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  setDoc, 
  serverTimestamp,
  getDoc
} from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db, auth } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';

const EstablishmentManagement = () => {
  const { createUser } = useAuth();
  const [establishments, setEstablishments] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEstablishment, setEditingEstablishment] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    nom: '',
    adresse: '',
    telephone: '',
    email: '',
    description: '',
    password: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchEstablishments();
  }, []);

  const fetchEstablishments = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'establishments'));
      const establishmentsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setEstablishments(establishmentsList);
    } catch (error) {
      showSnackbar('Erreur lors du chargement des établissements', 'error');
    }
  };

  const handleOpenDialog = (establishment = null) => {
    if (establishment) {
      setEditingEstablishment(establishment);
      setFormData({
        nom: establishment.nom,
        adresse: establishment.adresse,
        telephone: establishment.telephone,
        email: establishment.email || '',
        description: establishment.description || '',
        password: establishment.password || '',
      });
    } else {
      setEditingEstablishment(null);
      setFormData({
        nom: '',
        adresse: '',
        telephone: '',
        email: '',
        description: '',
        password: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEstablishment(null);
    setFormData({
      nom: '',
      adresse: '',
      telephone: '',
      email: '',
      description: '',
      password: '',
    });
    setShowPassword(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let establishmentId = null;
    let adminUser = null;
    let createdUserDoc = false;

    try {

      // Validation des données du formulaire
      if (!formData.email || !formData.password || !formData.nom || !formData.telephone) {
        console.error('Validation échouée - Champs manquants:', {
          email: !formData.email,
          password: !formData.password,
          nom: !formData.nom,
          telephone: !formData.telephone
        });
        showSnackbar('Veuillez remplir tous les champs obligatoires', 'error');
        return;
      }

      // 1. Créer d'abord l'établissement
      const establishmentData = {
        nom: formData.nom,
        adresse: formData.adresse,
        telephone: formData.telephone,
        email: formData.email,
        description: formData.description,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Ajouter l'établissement à la collection
      const docRef = await addDoc(collection(db, 'establishments'), establishmentData);
      establishmentId = docRef.id;

      // 2. Préparer les données admin
      const adminData = {
        email: formData.email,
        firstName: 'Admin',
        lastName: formData.nom,
        role: 'admin',
        phone: formData.telephone,
        department: 'Administration',
        createdBy: 'system',
        status: 'actif',
        establishmentId: establishmentId,
        isEstablishmentAdmin: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // 3. Créer le compte Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      adminUser = userCredential.user;

      // 4. Créer le document utilisateur avec l'UID
      const userDocRef = doc(db, 'users', adminUser.uid);
      await setDoc(userDocRef, {
        ...adminData,
        uid: adminUser.uid
      });
      createdUserDoc = true;

      // 5. Mettre à jour l'établissement avec l'ID de l'admin
      await updateDoc(doc(db, 'establishments', establishmentId), {
        adminUid: adminUser.uid
      });

      // 6. Attendre que le document utilisateur soit disponible
      let attempts = 0;
      const maxAttempts = 5;
      while (attempts < maxAttempts) {
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          break;
        }
        await new Promise(resolve => setTimeout(resolve, 1000));
        attempts++;
      }

      showSnackbar('Établissement et admin créés avec succès');
      handleCloseDialog();
      fetchEstablishments();
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      
      // Nettoyage en cas d'erreur
      try {
        if (createdUserDoc && adminUser) {
          await deleteDoc(doc(db, 'users', adminUser.uid));
        }
        if (adminUser) {
          await adminUser.delete();
        }
        if (establishmentId) {
          await deleteDoc(doc(db, 'establishments', establishmentId));
        }
      } catch (cleanupError) {
        console.error('Erreur lors du nettoyage:', cleanupError);
      }
      
      showSnackbar(error.message || 'Erreur lors de la création', 'error');
    }
  };

  const handleDelete = async (establishmentId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet établissement ?')) {
      try {
        // Récupérer les informations de l'établissement
        const establishmentDoc = establishments.find(e => e.id === establishmentId);
        
        // Supprimer l'établissement de Firestore
        await deleteDoc(doc(db, 'establishments', establishmentId));

        // Note: La suppression du compte Firebase Authentication devrait être gérée
        // par une fonction Cloud Firebase pour des raisons de sécurité

        showSnackbar('Établissement supprimé avec succès');
        fetchEstablishments();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        showSnackbar('Erreur lors de la suppression: ' + error.message, 'error');
      }
    }
  };

  return (
    <Box sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Gestion des Établissements</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Ajouter un établissement
        </Button>
      </Box>

      {establishments.map((establishment) => (
        <Box key={establishment.id} mb={3}>
          <Paper>
            <Box p={2}>
              <Typography variant="h6" component="h2">
                {establishment.nom}
              </Typography>
              <Typography color="textSecondary" gutterBottom>
                {establishment.adresse}
              </Typography>
              <Typography variant="body2">
                Téléphone: {establishment.telephone}
              </Typography>
              {establishment.email && (
                <Typography variant="body2">
                  Email: {establishment.email}
                </Typography>
              )}
              {establishment.description && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {establishment.description}
                </Typography>
              )}
            </Box>
            <Box display="flex" justifyContent="flex-end" p={2}>
              <IconButton 
                size="small" 
                onClick={() => handleOpenDialog(establishment)}
                color="primary"
              >
                <EditIcon />
              </IconButton>
              <IconButton 
                size="small" 
                onClick={() => handleDelete(establishment.id)}
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </Paper>
        </Box>
      ))}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingEstablishment ? 'Modifier l\'établissement' : 'Ajouter un établissement'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box mb={2}>
              <TextField
                required
                fullWidth
                label="Nom de l'établissement"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
              />
            </Box>
            <Box mb={2}>
              <TextField
                required
                fullWidth
                label="Adresse"
                name="adresse"
                value={formData.adresse}
                onChange={handleChange}
              />
            </Box>
            <Box mb={2}>
              <TextField
                required
                fullWidth
                label="Téléphone"
                name="telephone"
                value={formData.telephone}
                onChange={handleChange}
              />
            </Box>
            <Box mb={2}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </Box>
            <Box mb={2}>
              <TextField
                required
                fullWidth
                label="Mot de passe"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Box>
            <Box mb={2}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                multiline
                rows={4}
                value={formData.description}
                onChange={handleChange}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Annuler</Button>
            <Button type="submit" variant="contained" color="primary">
              {editingEstablishment ? 'Modifier' : 'Ajouter'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EstablishmentManagement;
